import { createRenderer } from 'fela';
import felaPluginEmbedded from 'fela-plugin-embedded';
import felaPluginFallbackValue from 'fela-plugin-fallback-value';
import felaPluginPlaceholderPrefixer from 'fela-plugin-placeholder-prefixer';
import felaPluginRtl from 'fela-plugin-rtl';
import * as React from 'react';
import { felaDisableAnimationsPlugin } from './felaDisableAnimationsPlugin';
import { felaExpandCssShorthandsPlugin } from './felaExpandCssShorthandsPlugin';
import { felaFocusVisibleEnhancer } from './felaFocusVisibleEnhancer';
import { felaInvokeKeyframesPlugin } from './felaInvokeKeyframesPlugin';
import { felaPerformanceEnhancer } from './felaPerformanceEnhancer';
import { felaSanitizeCssPlugin } from './felaSanitizeCssPlugin';
import { felaStylisEnhancer } from './felaStylisEnhancer';
import { RendererProvider } from './RendererProvider';
var felaDevMode = false;
try {
  // eslint-disable-next-line no-undef
  felaDevMode = process.env.NODE_ENV === 'development' || !!window.localStorage.felaDevMode;
} catch (_unused) {}
if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
  // if (felaDevMode) {
  //   /* eslint-disable-next-line no-console */
  //   console.warn(['@fluentui/react-northstar:', 'You are running Fela in development mode and this can cause performance degrades.', 'To disable it please paste `delete window.localStorage.felaDevMode` to your browsers console and reload current page.'].join(' '));
  // } else {
  //   /* eslint-disable-next-line no-console */
  //   console.warn(['@fluentui/react-northstar:', 'You are running Fela in production mode.', 'This limits your ability to edit styles in browsers development tools.', 'To enable development mode please paste `window.localStorage.felaDevMode = true` to your browsers console and reload the page.'].join(' '));
  // }
}
var blocklistedClassNames = [
// Blocklist contains a list of classNames that are used by FontAwesome
// https://fontawesome.com/how-to-use/on-the-web/referencing-icons/basic-use
'fa', 'fas', 'far', 'fal', 'fab',
// Used by https://github.com/fullcalendar/fullcalendar
'fc',
// .cke is used by CKEditor
'ck', 'cke'];
var filterClassName = function filterClassName(className) {
  // Also ensure that class name does not contain 'ad' as it might
  // cause compatibility issues regarding Ad blockers.
  return className.indexOf('ad') === -1 && blocklistedClassNames.indexOf(className) === -1;
};
var rendererConfig = {
  devMode: felaDevMode,
  filterClassName: filterClassName,
  enhancers: [felaPerformanceEnhancer, felaFocusVisibleEnhancer, felaStylisEnhancer],
  plugins: [felaDisableAnimationsPlugin,
  // is necessary to prevent accidental style typos
  // from breaking ALL the styles on the page
  felaSanitizeCssPlugin, felaPluginPlaceholderPrefixer(), felaInvokeKeyframesPlugin, felaPluginEmbedded(), felaExpandCssShorthandsPlugin,
  // Heads up!
  // This is required after fela-plugin-prefixer to resolve the array of fallback values prefixer produces.
  felaPluginFallbackValue(), felaPluginRtl()]
};
export function createFelaRenderer(options) {
  if (options === void 0) {
    options = {};
  }
  var _options = options,
    nonce = _options.nonce;
  return function () {
    var felaRenderer = createRenderer(rendererConfig);
    var usedRenderers = 0;
    if (nonce) {
      felaRenderer.styleNodeAttributes = {
        nonce: nonce
      };
    }

    // rehydration disabled to avoid leaking styles between renderers
    // https://github.com/rofrischmann/fela/blob/master/docs/api/fela-dom/rehydrate.md
    var Provider = function Provider(props) {
      return /*#__PURE__*/React.createElement(RendererProvider, {
        renderer: felaRenderer,
        rehydrate: true,
        targetDocument: props.target
      }, props.children);
    };
    return {
      registerUsage: function registerUsage() {
        usedRenderers += 1;
      },
      unregisterUsage: function unregisterUsage() {
        usedRenderers -= 1;
        if (usedRenderers === 0) {
          felaRenderer.listeners = [];
          felaRenderer.nodes = {};
          felaRenderer.updateSubscription = undefined;
        }
      },
      renderFont: function renderFont(font) {
        felaRenderer.renderFont(font.name, font.paths, font.props);
      },
      renderGlobal: felaRenderer.renderStatic,
      renderRule: function renderRule(styles, param) {
        var felaParam = Object.assign({}, param, {
          theme: {
            direction: param.direction
          }
        });
        return felaRenderer.renderRule(function () {
          return styles;
        }, felaParam);
      },
      // getOriginalRenderer() is implemented only for tests to be compatible with jest-react-fela expectations.
      getOriginalRenderer: function getOriginalRenderer() {
        // if (process.env.NODE_ENV !== 'test') {
        //   throw new Error('This method implements private API and can be used only in tests');
        // }
        return felaRenderer;
      },
      Provider: Provider
    };
  };
}
//# sourceMappingURL=createFelaRenderer.js.map
